import { Trans, useTranslation } from 'next-i18next/pages';
import Image from 'next/image';
import { useCallback, useEffect, useRef } from 'react';

import { useFlavorFromRoute } from '@pflegia/ui/hooks/use-flavor-from-route';
import { ReactComponent as Chevron } from '@pflegia/ui/icons/chevron.svg';

import { EMPLOYER_COUNT } from '@shared/constants/counts';

export interface EmployerLogo {
  title: string;
  src: string;
  alt?: string;
}

interface EmployersProps {
  delay?: number;
  employerCount?: number;
  employerLogos: EmployerLogo[];
  scrollOffset?: number;
  TitleComponent?: keyof React.JSX.IntrinsicElements;
}

const Employers: React.FC<EmployersProps> = ({
  delay = 3000,
  employerCount = EMPLOYER_COUNT,
  employerLogos,
  scrollOffset = 250,
  TitleComponent = 'h2',
}) => {
  const { t } = useTranslation('landingPage', { keyPrefix: 'employers' });
  const ref = useRef<HTMLDivElement>(null);
  const forward = useRef(true);
  const { sourceDomainName } = useFlavorFromRoute();

  const scroll = useCallback(() => {
    const offset = forward.current ? scrollOffset : scrollOffset * -1;
    if (ref?.current) ref.current.scrollTo(ref.current.scrollLeft + offset, 0);
  }, [scrollOffset]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (ref.current) {
        if (forward.current && ref.current.scrollWidth === ref.current.scrollLeft + ref.current.offsetWidth) {
          forward.current = false;
        } else if (!forward.current && ref.current.scrollLeft === 0) {
          forward.current = true;
        }
      }
      scroll();
    }, delay);

    return () => clearInterval(interval);
  }, [delay, scroll]);

  const handleScrollLeft = () => {
    forward.current = false;
    scroll();
  };

  const handleScrollRight = () => {
    forward.current = true;
    scroll();
  };

  return (
    <section className='container mx-auto flex flex-col items-center' data-testid='employers'>
      <TitleComponent className='mb-6 text-center text-2xl lg:mb-16 lg:text-4xl'>
        <Trans
          components={{ span: <span className='font-handwriting text-main-blue text-3xl lg:text-5xl' /> }}
          i18nKey='title'
          t={t}
          values={{ employerCount, domain: sourceDomainName }}
        />
      </TitleComponent>
      <div className='flex max-w-full'>
        <button aria-label={t('scrollLeft')} onClick={handleScrollLeft} type='button'>
          <Chevron className='text-main-blue rotate-180 lg:h-13.75 lg:w-13.75' />
        </button>
        <div className='scrollbar-hide flex gap-4 overflow-auto scroll-smooth' ref={ref} tabIndex={0}>
          {employerLogos.map(logo => (
            <Image
              alt={logo.alt ?? logo.title}
              className='h-20 w-24 shrink-0 object-contain lg:h-28 lg:w-48'
              height={112}
              key={logo.title}
              src={logo.src}
              width={192}
            />
          ))}
        </div>
        <button aria-label={t('scrollRight')} onClick={handleScrollRight} type='button'>
          <Chevron className='text-main-blue lg:h-13.75 lg:w-13.75' />
        </button>
      </div>
    </section>
  );
};

export default Employers;
